import { useQuery } from "@tanstack/react-query";
import { CommonAgentSummary, CommonAPI, SessionXAxis } from "../../types";
import {
  Card,
  CardGrid,
  FixedToolbarItem,
  Histogram,
  Toolbar,
  useAATheme,
  useReactSelectTheme,
} from "@bagel-web/components";
import { ButtonGroup, Spinner, ToggleButton } from "react-bootstrap";
import Select, { StylesConfig } from "react-select";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { getSessionHistogramDataByAgent } from "./dataTransformations";
import SessionTokenUsageChart from "./SessionTokenUsageChart";
import { LocalStorageKeys, useLocalStorage } from "../../util/localStorage";

type VisualizationOption = {
  value: string;
  label: string;
};

// A chart an app adds to the card's dropdown, alongside the shared ones.
export type ExtraVisualization = VisualizationOption & {
  render: (xAxis: SessionXAxis) => ReactNode;
};

const baseVisualizationOptions: Array<VisualizationOption> = [
  { value: "activity", label: "Activity by Agent" },
  { value: "tokens", label: "Token Usage" },
];

type SessionSummaryPreferences = {
  visualization: string;
  xAxis: SessionXAxis;
};

const xAxisOptions: Array<{ value: SessionXAxis; label: string }> = [
  { value: "moment", label: "Per Moment" },
  { value: "time", label: "Over Time" },
];

const selectStyleOverrides: StylesConfig<VisualizationOption, false> = {
  container: (baseStyles) => ({
    ...baseStyles,
    minWidth: "260px",
  }),
};

const SessionHistogram = ({
  sessionId,
  agents,
  commonAPI,
  extraVisualizations = [],
}: {
  sessionId: string;
  agents: Array<CommonAgentSummary>;
  commonAPI: CommonAPI;
  extraVisualizations?: Array<ExtraVisualization>;
}) => {
  const visualizationOptions = [
    ...baseVisualizationOptions,
    ...extraVisualizations,
  ];
  const storage = useLocalStorage<SessionSummaryPreferences>();
  const [preferences, setPreferences] = useState<SessionSummaryPreferences>(
    () => ({
      visualization: "activity",
      xAxis: "time",
      ...storage.getItem(LocalStorageKeys.SESSION_SUMMARY),
    })
  );
  useEffect(() => {
    storage.setItem(LocalStorageKeys.SESSION_SUMMARY, preferences);
  }, [preferences]);
  // Stored values this app doesn't offer fall back to the defaults.
  const xAxis = xAxisOptions.some(
    (option) => option.value === preferences.xAxis
  )
    ? preferences.xAxis
    : "time";
  const visualization = visualizationOptions.some(
    (option) => option.value === preferences.visualization
  )
    ? preferences.visualization
    : "activity";
  const extraVisualization = extraVisualizations.find(
    (option) => option.value === visualization
  );
  const selectTheme = useReactSelectTheme();

  const { data: sessionStats, isPending } = useQuery({
    queryKey: ["session-histogram", sessionId, xAxis],
    queryFn: () =>
      commonAPI.fetchSessionStats({ sessionId, numBuckets: 50, xAxis }),
    enabled: visualization === "activity",
  });

  const { isDarkMode } = useAATheme();
  const structuredSessionStats = useMemo(() => {
    return getSessionHistogramDataByAgent({
      agents,
      sessionStats,
      isDarkMode,
      xAxis,
    });
  }, [sessionStats, agents, isDarkMode, xAxis]);

  const router = useRouter();

  const handleClickElement = (dataSetIndex: number, index: number) => {
    const agentId =
      structuredSessionStats?.datasets?.[dataSetIndex]?.data[index]?.agentId;
    const moment =
      structuredSessionStats?.datasets?.[dataSetIndex]?.data[index]?.start
        .moment;
    if (agentId && moment) {
      router.push(`/sessions/${sessionId}/agent/${agentId}?momentId=${moment}`);
    }
  };

  return (
    <CardGrid className="mt-3" maxColumns={1}>
      <Card>
        <Toolbar className="mb-2" $justifyContent="flex-start">
          <FixedToolbarItem>
            <Select<VisualizationOption, false>
              aria-label="Visualization"
              value={visualizationOptions.find(
                (option) => option.value === visualization
              )}
              options={visualizationOptions}
              styles={selectStyleOverrides}
              theme={selectTheme}
              isClearable={false}
              isSearchable={false}
              onChange={(option) =>
                option &&
                setPreferences((prev) => ({
                  ...prev,
                  visualization: option.value,
                }))
              }
            />
          </FixedToolbarItem>
          <FixedToolbarItem>
            <ButtonGroup aria-label="X-axis">
              {xAxisOptions.map((option) => (
                <ToggleButton
                  key={option.value}
                  id={`session-summary-x-axis-${option.value}`}
                  value={option.value}
                  type="radio"
                  variant="outline-secondary"
                  checked={xAxis === option.value}
                  onChange={() =>
                    setPreferences((prev) => ({ ...prev, xAxis: option.value }))
                  }
                >
                  {option.label}
                </ToggleButton>
              ))}
            </ButtonGroup>
          </FixedToolbarItem>
        </Toolbar>
        {visualization === "activity" &&
          (isPending ? (
            <Spinner />
          ) : (
            <Histogram
              id="session-histogram"
              data={structuredSessionStats}
              xAxisTitle={xAxis === "moment" ? "Moment" : "Time"}
              onClickElement={handleClickElement}
            />
          ))}
        {visualization === "tokens" && (
          <SessionTokenUsageChart
            sessionId={sessionId}
            commonAPI={commonAPI}
            xAxis={xAxis}
          />
        )}
        {extraVisualization?.render(xAxis)}
      </Card>
    </CardGrid>
  );
};

export default SessionHistogram;
